/**
 * The `X-Auth-Client` header, and the one place that decides which requests carry it.
 *
 * Both back ends the console talks to are shared with the customer-facing frontend, and both
 * need to be told which of the two clients is calling:
 *  - the gatherco/Node APIs, where it scopes the server to the console's own (backend) cookie so
 *    it never falls back to the frontend cookie when both apps are logged in on the same host;
 *  - the AI API (`environment.aiApiUrl`), which rejects a request without it as `401 Unauthorized`
 *    even when the Bearer service token is valid.
 *
 * Everything on the AI host must send it, so it is applied centrally in
 * `CredentialsInterceptor` for HttpClient traffic and by the two SSE clients that have to use
 * `fetch` (chat + email agent streaming) rather than per call site.
 */

/** Header the APIs identify the calling client with. */
export const AUTH_CLIENT_HEADER = 'X-Auth-Client';

/** This app's client identity. The customer-facing frontend sends its own value. */
export const AUTH_CLIENT = 'backend';

/**
 * True when `url` targets the AI API, i.e. the host in `environment.aiApiUrl`.
 *
 * An empty `aiApiUrl` means "same origin" (local dev, where `/pebbl` is proxied to the local AI
 * backend by proxy.conf.json), so in that case only the proxied AI paths count — not every
 * relative URL the app happens to fetch.
 */
export function isAiApiUrl(url: string, aiApiUrl: string): boolean {
  const root = (aiApiUrl || '').replace(/\/+$/, '');
  if (root) {
    return url.startsWith(root);
  }
  const path = url.replace(/^\.?\//, '');
  return path === 'pebbl' || path.startsWith('pebbl/');
}
